import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import SelectBox, { GroupedSelectBox } from '@adsk/alloy-select-box';
import TextInput from '@adsk/alloy-react-text-input';
import FormField from '@adsk/alloy-react-form-field';
import { DatePicker } from '@adsk/alloy-react-date-picker';
import Modal, { ModalConfirmation } from '@adsk/alloy-react-modal';
import Divider from '@adsk/alloy-react-divider';
import { useLocale } from '@adsk/alloy-react-locale';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { AlertInformationIcon } from '@adsk/alloy-react-icon';
import { InfoPopover } from '@adsk/alloy-react-tooltip';
import {
  AutocompleteOrManualAddress,
  DatePickerCustomFooter,
  Label,
} from '@adsk/acs-admin-ui-components-shared-components';
import {
  getTimeZones,
  getCurrencies,
  mapProjectTypesForSelectBox,
  mapTemplatesToSelectGroupOptions,
} from '@adsk/acs-admin-ui-components-helpers';
import ModalFooter from './ModalFooter';
import {
  modalStyle,
  modalBodyStyle,
  fieldStyle,
  firstColumnFieldStyle,
  secondColumnFieldStyle,
  secondColumnNoLabelFieldStyle,
  flexWrapperStyle,
  timezoneTooltipLinkStyle,
  loaderContainerStyle,
  loaderBoxStyle,
  ringCustomStyle,
  loaderTextStyle,
  templateLabelContainer,
  templateLabelInfoPopover,
  templateLabelInfoPopoverIcon,
  templateTooltipLinkDividerStyle,
  templateTooltipLinkStyle,
  templateGroupLabel,
} from './styles';
import { TEMPLATE_TOOLTIP_LINK } from './constants';

const CreateProjectModalView = ({
  accountId,
  accounts,
  additionalFieldsActivated,
  errors,
  isEmsAccount,
  isFetchingAccounts,
  isFetchingProjectTypes,
  isFetchingTemplates,
  isTemplatesMaintenanceMode,
  mapBoxAccessToken,
  onBlur,
  onChange,
  onChangeAccount,
  onChangeAddress,
  onChangeAddressInputMode,
  onChangeAutocompletedAddress,
  onChangeCurrency,
  onChangeEndDate,
  onChangeProjectType,
  onChangeProjectValue,
  onChangeStartDate,
  onChangeTemplate,
  onChangeTimeZone,
  onClickCancel,
  onCloseModal,
  onModalConfirmationCancel,
  onModalConfirmationConfirm,
  onSubmitCreateProject,
  projectTypes,
  setMapboxTokenActive,
  show,
  templateId,
  templates,
  values,
}) => {
  const { T, locale: localeName } = useLocale(({ locale }) =>
    require(`./i18n/${locale}.json`)
  );
  const maxStartDate = values.endDate
    ? new Date(
        new Date(values.endDate.getTime()).setDate(values.endDate.getDate() - 1)
      )
    : null;
  const minEndDate = values.startDate
    ? new Date(
        new Date(values.startDate.getTime()).setDate(
          values.startDate.getDate() + 1
        )
      )
    : null;
  const formattedProjectTypes = useMemo(
    () => mapProjectTypesForSelectBox(projectTypes, localeName),
    [projectTypes, localeName]
  );
  const templateOptions = useMemo(
    () =>
      isFetchingTemplates
        ? []
        : mapTemplatesToSelectGroupOptions(templates, localeName),
    [templates, isFetchingTemplates, localeName]
  );

  let templatesPlaceHolder = T.translate('TEMPLATE_ORG_SELECT_PLACEHOLDER');
  if (isTemplatesMaintenanceMode) {
    templatesPlaceHolder = T.translate('TEMPLATES_NOT_AVAILABLE');
  } else if (values.selectedAccount) {
    templatesPlaceHolder = T.translate('TEMPLATE_SELECT_PLACEHOLDER');
  }

  const timeZones = useMemo(() => getTimeZones(localeName), [localeName]);
  const currencies = getCurrencies();

  const datesWrapperRef = useRef();
  const nameInputRef = useRef();
  const modalRef = useRef();
  const latestCancelClickHandler = useRef(onClickCancel);

  useEffect(() => {
    latestCancelClickHandler.current = onClickCancel;
  }, [onClickCancel]);

  // Set focus when displating the duplicated name error
  useEffect(() => {
    if (errors.projectName && nameInputRef.current)
      nameInputRef.current.focus();
  }, [errors.projectName]);

  // Handle ESC key and set focus when opening the modal
  useEffect(() => {
    const handleEscKeyPressed = ({ key }) => {
      if (key === 'Escape') latestCancelClickHandler.current();
    };

    if (show) {
      document.addEventListener('keydown', handleEscKeyPressed, false);

      if (nameInputRef.current) nameInputRef.current.focus();
    }

    return () => {
      if (show)
        document.removeEventListener('keydown', handleEscKeyPressed, false);
    };
  }, [show]);

  return (
    <>
      <Modal
        open={values.showModalConfirmation ? false : show}
        style={modalStyle}
        ref={modalRef}
        data-testid="CreateProjectModal_modal"
      >
        <Modal.Header>
          {T.translate('TITLE')}
          <Modal.Close onClick={onCloseModal} />
        </Modal.Header>
        <Modal.Body style={modalBodyStyle}>
          <FormField
            error={errors.projectName}
            errorVariant={FormField.ERROR_VARIANTS.TOP}
            label={T.translate('NAME_LABEL')}
            required
            labelVariant={FormField.LABEL_VARIANTS.TOP}
            style={fieldStyle}
            data-testid="CreateProjectModal_name_field"
          >
            <TextInput
              error={errors.projectName != null}
              data-testid="CreateProjectModal_name_field__textInput"
              id="projectName"
              name="projectName"
              onBlur={onBlur}
              onChange={onChange}
              placeholder={T.translate('NAME_PLACEHOLDER')}
              ref={nameInputRef}
              type="text"
              value={values.projectName}
            />
          </FormField>
          {additionalFieldsActivated && (
            <FormField
              label={T.translate('JOB_NUMBER_LABEL')}
              labelVariant={FormField.LABEL_VARIANTS.TOP}
              style={fieldStyle}
              data-testid="CreateProjectModal_job_number_field"
            >
              <TextInput
                id="jobNumber"
                name="jobNumber"
                onBlur={onBlur}
                onChange={onChange}
                placeholder={T.translate('JOB_NUMBER_PLACEHOLDER')}
                type="text"
                value={values.jobNumber}
              />
            </FormField>
          )}
          {!isEmsAccount && (
            <>
              <FormField
                label={T.translate('ORG_LABEL')}
                labelVariant={FormField.LABEL_VARIANTS.TOP}
                required
                style={fieldStyle}
                data-testid="CreateProjectModal__org_field"
              >
                <SelectBox
                  container={modalRef.current}
                  maxMenuHeight={180}
                  creatable={false}
                  isLoading={isFetchingAccounts}
                  onChange={onChangeAccount}
                  options={accounts}
                  disabled={!!accountId}
                  labelKey="name"
                  valueKey="id"
                  placeholder={T.translate('ORG_PLACEHOLDER')}
                  value={values.selectedAccount}
                />
              </FormField>
              <FormField
                label={T.translate('PROJECT_TYPE')}
                labelVariant={FormField.LABEL_VARIANTS.TOP}
                required
                style={fieldStyle}
                data-testid="CreateProjectModal_project_type_field"
              >
                <GroupedSelectBox
                  container={modalRef.current}
                  data-testid="CreateProjectModal__GroupedSelectBox"
                  maxMenuHeight={300}
                  options={formattedProjectTypes}
                  multiple={false}
                  disabled={!values.selectedAccount}
                  isLoading={isFetchingProjectTypes || isFetchingAccounts}
                  onChange={onChangeProjectType}
                  value={values.selectedProjectType}
                  placeholder={
                    values.selectedAccount
                      ? T.translate('PROJECT_TYPE_PLACEHOLDER')
                      : T.translate('TEMPLATE_ORG_SELECT_PLACEHOLDER')
                  }
                  searchable
                >
                  {(props) => <SelectBox {...props} />}
                </GroupedSelectBox>
              </FormField>
              <FormField
                label={
                  <div style={templateLabelContainer}>
                    {T.translate('TEMPLATE_LABEL')}
                    <InfoPopover
                      message={
                        <div>
                          <div>{T.translate('TEMPLATE_TOOLTIP')}</div>
                          <Divider
                            style={templateTooltipLinkDividerStyle}
                            color={Divider.COLORS.DARK}
                          />
                          <a
                            href={TEMPLATE_TOOLTIP_LINK}
                            style={templateTooltipLinkStyle}
                          >
                            {T.translate('LEARN_MORE')}
                          </a>
                        </div>
                      }
                      variant="dark"
                      style={templateLabelInfoPopover}
                      renderIcon={(p) => (
                        <AlertInformationIcon
                          {...p}
                          style={templateLabelInfoPopoverIcon}
                        />
                      )}
                    />
                  </div>
                }
                labelVariant={FormField.LABEL_VARIANTS.TOP}
                style={fieldStyle}
                data-testid="CreateProjectModal_template_field"
              >
                <GroupedSelectBox
                  container={modalRef.current}
                  options={templateOptions}
                  labelKey="name"
                  valueKey="id"
                  value={values.selectedTemplate}
                  disabled={
                    isTemplatesMaintenanceMode ||
                    !values.selectedAccount ||
                    !!templateId
                  }
                  isLoading={isFetchingTemplates || isFetchingAccounts}
                  onChange={onChangeTemplate}
                  groupLabelGetter={(group) => (
                    <div style={templateGroupLabel.container}>
                      <div style={templateGroupLabel.title}>{group.label}</div>
                      <div>{group.subtext}</div>
                    </div>
                  )}
                  placeholder={templatesPlaceHolder}
                  clearable
                  searchable
                />
              </FormField>
              <FormField
                label={T.translate('ADDRESS_LABEL')}
                labelVariant={FormField.LABEL_VARIANTS.TOP}
                style={fieldStyle}
                data-testid="CreateProjectModal_address_field"
              >
                <AutocompleteOrManualAddress
                  address={values.address}
                  autocompletedAddress={values.autocompletedAddress}
                  inputMode={values.addressInputMode}
                  isInModal
                  isMapboxTokenActive={values.isMapboxTokenActive}
                  mapBoxAccessToken={mapBoxAccessToken}
                  onAddressChange={onChangeAddress}
                  onAutocompletedAddressResult={onChangeAutocompletedAddress}
                  onInputModeChange={onChangeAddressInputMode}
                  resetAddressOnDelete={false}
                  setMapboxTokenActive={setMapboxTokenActive}
                />
              </FormField>
            </>
          )}
          {additionalFieldsActivated && !isEmsAccount && (
            <div
              style={fieldStyle}
              data-testid="CreateProjectModal_time_zone_field"
            >
              <Label
                text={T.translate('TIME_ZONE_LABEL')}
                tooltipMessage={
                  <div>
                    <div>{T.translate('TIME_ZOME_TOOLTIP')}</div>
                    <Divider
                      style={{ margin: '10px 0' }}
                      color={Divider.COLORS.DARK}
                    />
                    <a
                      href="https://help.autodesk.com/view/DOCS/ENU/?guid=Project_Settings"
                      style={timezoneTooltipLinkStyle}
                    >
                      {T.translate('LEARN_MORE')}
                    </a>
                  </div>
                }
                delayTooltipHide
              />
              <SelectBox
                container={modalRef.current}
                options={timeZones}
                value={values.selectedTimeZone}
                onChange={onChangeTimeZone}
                placeholder={T.translate('TIME_ZOME_SELECT_PLACEHOLDER')}
                clearable
                searchable
                showSearchIcon
              />
            </div>
          )}
          <div
            data-testid="CreateProjectModal_date_fields"
            ref={datesWrapperRef}
            style={{ display: 'flex' }}
          >
            <div style={firstColumnFieldStyle}>
              <Label
                text={T.translate('START_DATE_LABEL')}
                tooltipMessage={T.translate('DATES_TOOLTIP')}
              />
              <DatePicker
                onChange={onChangeStartDate}
                value={values.startDate}
                container={datesWrapperRef.current}
                maxDate={maxStartDate}
                triggerProps={{ style: { width: '100%' } }}
                renderFooter={DatePickerCustomFooter}
              />
            </div>
            <div style={secondColumnFieldStyle}>
              <Label
                text={T.translate('END_DATE_LABEL')}
                tooltipMessage={T.translate('DATES_TOOLTIP')}
              />
              <DatePicker
                onChange={onChangeEndDate}
                value={values.endDate}
                container={datesWrapperRef.current}
                minDate={minEndDate}
                triggerProps={{ style: { width: '100%' } }}
                renderFooter={DatePickerCustomFooter}
              />
            </div>
          </div>
          {additionalFieldsActivated && !isEmsAccount && (
            <div
              style={flexWrapperStyle}
              data-testid="CreateProjectModal_project_value_field"
            >
              <FormField
                error={errors.projectValue}
                errorVariant={FormField.ERROR_VARIANTS.TOP}
                label={T.translate('PROJECT_VALUE_LABEL')}
                labelVariant={FormField.LABEL_VARIANTS.TOP}
                style={firstColumnFieldStyle}
              >
                <TextInput
                  error={errors.projectValue != null}
                  onChange={onChangeProjectValue}
                  placeholder={T.translate('PROJECT_VALUE_PLACEHOLDER')}
                  type="text"
                  value={values.projectValue}
                />
              </FormField>
              <div style={secondColumnNoLabelFieldStyle}>
                <SelectBox
                  container={modalRef.current}
                  options={currencies}
                  value={values.selectedCurrency}
                  onChange={onChangeCurrency}
                />
              </div>
            </div>
          )}
          {values.isSaving && (
            <div style={loaderContainerStyle}>
              <div style={loaderBoxStyle}>
                <ProgressRing style={ringCustomStyle} />
                <div style={loaderTextStyle}>
                  {T.translate('CREATING_PROJECT_INDICATOR_TEXT')}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <ModalFooter
            onCancelClick={onClickCancel}
            onCreateClick={onSubmitCreateProject}
            createDisabled={values.missingRequiredField}
            isSaving={values.isSaving}
            submitButtonLabel={T.translate('FOOTER_CONFIRM_BUTTON')}
          />
        </Modal.Footer>
      </Modal>
      <ModalConfirmation
        data-testid="CreateProjectModal__ModalConfirmation"
        show={values.showModalConfirmation}
        title={T.translate('CANCEL_CONFIRMATION_TITLE')}
        message={T.translate('CANCEL_CONFIRMATION_MESSAGE')}
        cancelButtonLabel={T.translate('CANCEL_CONFIRMATION_BACK_BUTTON')}
        confirmationButtonLabel={T.translate(
          'CANCEL_CONFIRMATION_CANCEL_BUTTON'
        )}
        onConfirm={onModalConfirmationConfirm}
        onClose={onModalConfirmationCancel}
        disableModalExit
        isConfirmationButtonDisabled={false}
        isOnConfirmInProgress={false}
        hideOnConfirm={false}
        noCancelButton={false}
      />
    </>
  );
};

CreateProjectModalView.propTypes = {
  accounts: PropTypes.arrayOf(PropTypes.object).isRequired,
  additionalFieldsActivated: PropTypes.bool,
  errors: PropTypes.shape({
    projectName: PropTypes.string,
    projectValue: PropTypes.string,
  }).isRequired,
  isEmsAccount: PropTypes.bool,
  isFetchingAccounts: PropTypes.bool.isRequired,
  isFetchingProjectTypes: PropTypes.bool.isRequired,
  isFetchingTemplates: PropTypes.bool.isRequired,
  isTemplatesMaintenanceMode: PropTypes.bool,
  mapBoxAccessToken: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onChangeAccount: PropTypes.func.isRequired,
  onChangeAddress: PropTypes.func.isRequired,
  onChangeAddressInputMode: PropTypes.func.isRequired,
  onChangeAutocompletedAddress: PropTypes.func.isRequired,
  onChangeCurrency: PropTypes.func.isRequired,
  onChangeEndDate: PropTypes.func.isRequired,
  onChangeProjectType: PropTypes.func.isRequired,
  onChangeProjectValue: PropTypes.func.isRequired,
  onChangeStartDate: PropTypes.func.isRequired,
  onChangeTemplate: PropTypes.func.isRequired,
  onChangeTimeZone: PropTypes.func.isRequired,
  onClickCancel: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onModalConfirmationCancel: PropTypes.func.isRequired,
  onModalConfirmationConfirm: PropTypes.func.isRequired,
  onSubmitCreateProject: PropTypes.func.isRequired,
  projectTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  setMapboxTokenActive: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  values: PropTypes.shape({
    address: PropTypes.objectOf(PropTypes.string),
    addressInputMode: PropTypes.string,
    autocompletedAddress: PropTypes.string,
    endDate: PropTypes.instanceOf(Date),
    isMapboxTokenActive: PropTypes.bool,
    isSaving: PropTypes.bool,
    jobNumber: PropTypes.string,
    missingRequiredField: PropTypes.bool,
    projectName: PropTypes.string,
    projectValue: PropTypes.string,
    selectedAccount: PropTypes.shape({
      accessLevels: PropTypes.objectOf(PropTypes.bool),
      createProjectAccessLevels: PropTypes.arrayOf(PropTypes.string),
      hasAcsUnifiedProjects: PropTypes.bool,
      id: PropTypes.string,
      isAccEligible: PropTypes.bool,
      name: PropTypes.string,
    }),
    selectedCurrency: PropTypes.objectOf(PropTypes.string),
    selectedPhase: PropTypes.objectOf(PropTypes.string),
    selectedProjectType: PropTypes.objectOf(PropTypes.string),
    selectedTemplate: PropTypes.objectOf(PropTypes.string),
    selectedTimeZone: PropTypes.objectOf(PropTypes.string),
    showModalConfirmation: PropTypes.bool,
    startDate: PropTypes.instanceOf(Date),
  }).isRequired,
};

CreateProjectModalView.defaultProps = {
  isEmsAccount: false,
  additionalFieldsActivated: false,
  isTemplatesMaintenanceMode: false,
};

export default CreateProjectModalView;
